/**
 * Agents — the live control surface for Quill's conversational agents
 * (interview, mirror-listener, memory, …). Backend already exists
 * (routes/agent.js, mounted at /api/admin/quill/agent); this page is the
 * frontend the P5 Wave 4 contract §1 specifies.
 *
 * Four metric cards (active sessions · completed 7d · abandoned 7d · stuck
 * >30m) summarise the fleet. The registry table lists every agent with an
 * enable/disable toggle, a model-override select, its live active count, its
 * 7-day completion rate, and a health pill. Below it, the stuck-session list
 * surfaces active sessions silent for 30+ minutes, each with an Abandon action.
 *
 * Every mutation (enable/disable, model override, abandon) is a PRODUCTION
 * write that changes live member conversation behaviour, so each routes through
 * QuillActionConfirm with explicit copy before the PATCH/POST fires. Toggles
 * and selects are controlled off the last-fetched server state — the underlying
 * value only moves after a confirmed write + refetch, so a cancelled dialog
 * leaves the row untouched. Each section loads independently
 * (Promise.allSettled) and renders its own error + Retry — an outage is never
 * silently shown as "no data".
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  Button,
  Chip,
  Switch,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useQuillHealth } from '../QuillHealthContext';
import MetricCard from '../../common/MetricCard';
import DataTable, { Column } from '../../common/DataTable';
import EmptyState from '../../common/EmptyState';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors, typography } from '../../../theme/designTokens';
import {
  agentsApi,
  AgentDefinition,
  AgentHealthRow,
  AgentSession,
} from './api';

/** Coerce a node-pg count (bigint arrives as a string) to a number. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Curated model-override options. The engine default (no override) is the empty
// value; the live list is unioned with any override already set on an agent so
// a currently-applied override never disappears from its own dropdown.
const COMMON_MODEL_OVERRIDES = [
  'grok-4',
  'grok-4-fast',
  'grok-3',
  'grok-3-mini',
  'claude-opus-4',
  'claude-sonnet-4',
  'claude-haiku-4',
];

const STUCK_MINUTES = 30;

/** Small inline error+Retry block for a single section (reuses MUI Alert). */
const SectionError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <Alert
    severity="error"
    action={
      <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>
        Retry
      </Button>
    }
  >
    {message}
  </Alert>
);

type StatusColor = 'default' | 'success' | 'warning' | 'error';

/** Health pill derived from enablement + live/7-day activity. */
function agentHealth(def: AgentDefinition, health?: AgentHealthRow): { label: string; color: StatusColor } {
  if (!def.is_enabled) return { label: 'Disabled', color: 'default' };
  const stuck = num(health?.stuck_count);
  if (stuck > 0) return { label: `Stuck ${stuck}`, color: 'warning' };
  const total = num(def.total_7d);
  const abandoned = num(def.abandoned_7d);
  if (total > 0 && abandoned / total > 0.3) return { label: 'High abandon', color: 'warning' };
  if (num(def.active_sessions) > 0 || total > 0) return { label: 'Healthy', color: 'success' };
  return { label: 'Idle', color: 'default' };
}

// The mutation the confirm dialog is about to perform.
type PendingAction =
  | { kind: 'toggle'; agent: AgentDefinition; nextEnabled: boolean }
  | { kind: 'override'; agent: AgentDefinition; nextOverride: string | null }
  | { kind: 'abandon'; session: AgentSession };

const AgentsPage: React.FC = () => {
  const { status } = useQuillHealth();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [definitions, setDefinitions] = useState<AgentDefinition[]>([]);
  const [definitionsLoading, setDefinitionsLoading] = useState(true);
  const [definitionsError, setDefinitionsError] = useState<string | null>(null);

  const [health, setHealth] = useState<AgentHealthRow[]>([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [mutating, setMutating] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const loadDefinitions = useCallback(async () => {
    setDefinitionsLoading(true);
    setDefinitionsError(null);
    try {
      const data = await agentsApi.getDefinitions();
      if (mountedRef.current) setDefinitions(data);
    } catch (e) {
      if (mountedRef.current)
        setDefinitionsError(e instanceof Error ? e.message : 'Failed to load agent registry.');
    } finally {
      if (mountedRef.current) setDefinitionsLoading(false);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const data = await agentsApi.getHealth();
      if (mountedRef.current) setHealth(data);
    } catch (e) {
      if (mountedRef.current)
        setHealthError(e instanceof Error ? e.message : 'Failed to load agent health.');
    } finally {
      if (mountedRef.current) setHealthLoading(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await agentsApi.getSessions({ status: 'active,suspended', limit: 200 });
      if (mountedRef.current) setSessions(data);
    } catch (e) {
      if (mountedRef.current)
        setSessionsError(e instanceof Error ? e.message : 'Failed to load live sessions.');
    } finally {
      if (mountedRef.current) setSessionsLoading(false);
    }
  }, []);

  const loadAll = useCallback(() => {
    Promise.allSettled([loadDefinitions(), loadHealth(), loadSessions()]);
  }, [loadDefinitions, loadHealth, loadSessions]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const healthByAgent = useMemo(() => {
    const m = new Map<string, AgentHealthRow>();
    health.forEach((h) => m.set(h.agent_name, h));
    return m;
  }, [health]);

  // Stuck sessions: active and silent for STUCK_MINUTES+. Derived from the live
  // sessions list so the metric card count and the list below never disagree.
  const stuckSessions = useMemo(() => {
    const cutoff = Date.now() - STUCK_MINUTES * 60_000;
    return sessions.filter((s) => {
      if (s.status !== 'active') return false;
      if (!s.last_activity_at) return false;
      const t = new Date(s.last_activity_at).getTime();
      return Number.isFinite(t) && t < cutoff;
    });
  }, [sessions]);

  const totals = useMemo(() => {
    let active = 0;
    let completed = 0;
    let abandoned = 0;
    definitions.forEach((d) => {
      active += num(d.active_sessions);
      completed += num(d.completed_7d);
      abandoned += num(d.abandoned_7d);
    });
    return { active, completed, abandoned };
  }, [definitions]);

  const overrideOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const push = (m?: string | null) => {
      if (m && !seen.has(m)) {
        seen.add(m);
        out.push(m);
      }
    };
    COMMON_MODEL_OVERRIDES.forEach(push);
    definitions.forEach((d) => push(d.model_override));
    return out;
  }, [definitions]);

  const agentLabel = (d: AgentDefinition) => d.display_name || d.agent_name;

  // ── Confirm + commit ─────────────────────────────────────────────────
  const runPending = async () => {
    if (!pending) return;
    setMutating(true);
    setMutationError(null);
    try {
      if (pending.kind === 'toggle') {
        await agentsApi.updateAgent(pending.agent.agent_name, { is_enabled: pending.nextEnabled });
      } else if (pending.kind === 'override') {
        await agentsApi.updateAgent(pending.agent.agent_name, { model_override: pending.nextOverride });
      } else {
        await agentsApi.abandonSession(pending.session.session_id);
      }
      if (!mountedRef.current) return;
      setPending(null);
      // Refetch the affected surfaces so the UI reflects the committed state.
      if (pending.kind === 'abandon') {
        loadSessions();
      } else {
        loadDefinitions();
        loadHealth();
      }
    } catch (e) {
      if (mountedRef.current)
        setMutationError(e instanceof Error ? e.message : 'The change could not be saved.');
    } finally {
      if (mountedRef.current) setMutating(false);
    }
  };

  const closeDialog = () => {
    if (mutating) return;
    setPending(null);
    setMutationError(null);
  };

  // ── Registry table columns ───────────────────────────────────────────
  const registryColumns: Column<AgentDefinition>[] = [
    {
      id: 'agent_name',
      label: 'Agent',
      render: (row) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: typography.sizes.sm }}>
            {agentLabel(row)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: typography.fonts.mono, color: 'text.disabled', display: 'block' }}
          >
            {row.agent_name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'is_enabled',
      label: 'Enabled',
      align: 'center',
      render: (row) => (
        <Switch
          checked={!!row.is_enabled}
          onChange={(_e, checked) =>
            setPending({ kind: 'toggle', agent: row, nextEnabled: checked })
          }
          slotProps={{ input: { 'aria-label': `Toggle ${agentLabel(row)}` } }}
        />
      ),
    },
    {
      id: 'model_override',
      label: 'Model override',
      render: (row) => (
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            value={row.model_override || ''}
            displayEmpty
            onChange={(e) => {
              const v = (e.target.value as string) || null;
              if ((row.model_override || null) === v) return; // no-op
              setPending({ kind: 'override', agent: row, nextOverride: v });
            }}
            slotProps={{ input: { 'aria-label': `Model override for ${agentLabel(row)}` } }}
            sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm }}
          >
            <MenuItem value="">
              <em>Engine default</em>
            </MenuItem>
            {overrideOptions.map((m) => (
              <MenuItem key={m} value={m} sx={{ fontFamily: typography.fonts.mono }}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      id: 'active_sessions',
      label: 'Live',
      align: 'right',
      render: (row) => num(row.active_sessions).toLocaleString(),
    },
    {
      id: 'completed_7d',
      label: '7d completion',
      align: 'right',
      render: (row) => {
        const total = num(row.total_7d);
        const completed = num(row.completed_7d);
        if (total === 0) return '—';
        return (
          <Box>
            <Typography sx={{ fontSize: typography.sizes.sm, fontWeight: 600 }}>
              {Math.round((completed / total) * 100)}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {completed.toLocaleString()} / {total.toLocaleString()}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'health',
      label: 'Health',
      align: 'center',
      render: (row) => {
        const h = agentHealth(row, healthByAgent.get(row.agent_name));
        return <Chip label={h.label} size="small" color={h.color} variant={h.color === 'default' ? 'outlined' : 'filled'} />;
      },
    },
  ];

  const stuckColumns: Column<AgentSession>[] = [
    {
      id: 'session_id',
      label: 'Session',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, wordBreak: 'break-all' }}>
          {row.session_id}
        </Typography>
      ),
    },
    {
      id: 'agent_name',
      label: 'Agent',
      render: (row) => row.agent_display_name || row.agent_name,
    },
    {
      id: 'quill_member_id',
      label: 'Member',
      render: (row) => (
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm, wordBreak: 'break-all' }}>
          {row.quill_member_id}
        </Typography>
      ),
    },
    {
      id: 'events_count',
      label: 'Events',
      align: 'right',
      render: (row) => num(row.events_count).toLocaleString(),
    },
    {
      id: 'last_activity_at',
      label: 'Idle',
      align: 'right',
      render: (row) => {
        if (!row.last_activity_at) return '—';
        const mins = Math.floor((Date.now() - new Date(row.last_activity_at).getTime()) / 60_000);
        if (!Number.isFinite(mins)) return '—';
        return `${mins} min`;
      },
    },
    {
      id: 'abandon',
      label: '',
      align: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          color="error"
          sx={{ textTransform: 'none' }}
          onClick={() => setPending({ kind: 'abandon', session: row })}
        >
          Abandon
        </Button>
      ),
    },
  ];

  // ── Confirm dialog copy per action ───────────────────────────────────
  const dialogProps = useMemo(() => {
    if (!pending) return null;
    if (pending.kind === 'toggle') {
      const enabling = pending.nextEnabled;
      return {
        title: enabling ? 'Enable agent' : 'Disable agent',
        destructive: !enabling,
        confirmLabel: enabling ? 'Enable' : 'Disable',
        message: (
          <>
            {enabling ? 'Enable' : 'Disable'} <strong>{agentLabel(pending.agent)}</strong> (
            <code>{pending.agent.agent_name}</code>)?
          </>
        ),
        caption:
          'This is a production change — it changes live member conversation behaviour. The engine picks it up within ~60 seconds (config cache).',
      };
    }
    if (pending.kind === 'override') {
      const clearing = pending.nextOverride === null;
      return {
        title: clearing ? 'Clear model override' : 'Set model override',
        destructive: false,
        confirmLabel: clearing ? 'Clear override' : 'Set override',
        message: clearing ? (
          <>
            Clear the model override for <strong>{agentLabel(pending.agent)}</strong>? It will fall
            back to the engine default model.
          </>
        ) : (
          <>
            Point <strong>{agentLabel(pending.agent)}</strong> at{' '}
            <code>{pending.nextOverride}</code>?
          </>
        ),
        caption:
          'This is a production change — it changes which model answers live member conversations. The engine picks it up within ~60 seconds (config cache).',
      };
    }
    // abandon
    return {
      title: 'Abandon session',
      destructive: true,
      confirmLabel: 'Abandon session',
      message: (
        <>
          Force-abandon session <code>{pending.session.session_id}</code> on{' '}
          <strong>{pending.session.agent_display_name || pending.session.agent_name}</strong>?
        </>
      ),
      caption:
        'This ends a live member conversation immediately and cannot be undone from here. Use only for sessions that are genuinely stuck.',
    };
  }, [pending]);

  const anyLoading = definitionsLoading || healthLoading || sessionsLoading;
  const registryEmpty = !definitionsLoading && !definitionsError && definitions.length === 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: `${colors.deepTeal[500]}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          <SmartToyIcon sx={{ fontSize: 28, color: colors.deepTeal[500] }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: typography.fonts.display, fontSize: '1.75rem', fontWeight: 700, color: colors.gray[900] }}>
            Agents
          </Typography>
          <Typography sx={{ fontFamily: typography.fonts.body, fontSize: typography.sizes.sm, color: 'text.secondary' }}>
            Quill's conversational agents — enablement, model overrides, live sessions, and stuck-session recovery.
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● {status === 'up' ? 'Connected' : status}
      </Typography>

      {mutationError && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error" onClose={() => setMutationError(null)}>
            {mutationError}
          </Alert>
        </Box>
      )}

      {/* Metric cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Active sessions" value={String(totals.active)} color="users" icon="chat" loading={definitionsLoading} subtitle="Live now" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Completed (7d)" value={String(totals.completed)} color="revenue" icon="success" loading={definitionsLoading} subtitle="Last 7 days" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title="Abandoned (7d)" value={String(totals.abandoned)} color="costs" icon="warning" loading={definitionsLoading} subtitle="Last 7 days" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard title={`Stuck (>${STUCK_MINUTES}m)`} value={String(stuckSessions.length)} color="content" icon="timer" loading={sessionsLoading} subtitle="Active, silent" />
        </Grid>
      </Grid>

      {/* Registry */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
          Agent registry
        </Typography>
        {definitionsError ? (
          <SectionError message={definitionsError} onRetry={loadDefinitions} />
        ) : registryEmpty ? (
          <EmptyState
            icon="ai_bot"
            title="No agents registered"
            description="The quill_agent_config table has no rows yet, or the engine has not seeded its agents."
          />
        ) : (
          <>
            {healthError && (
              <Box sx={{ mb: 1.5 }}>
                <SectionError message={`Health metrics unavailable: ${healthError}`} onRetry={loadHealth} />
              </Box>
            )}
            <DataTable<AgentDefinition>
              columns={registryColumns}
              data={definitions}
              loading={definitionsLoading}
              emptyMessage="No agents registered"
              emptyIcon="ai_bot"
            />
          </>
        )}
      </Box>

      {/* Stuck sessions */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg, mb: 2 }}>
          Stuck sessions
        </Typography>
        {sessionsError ? (
          <SectionError message={sessionsError} onRetry={loadSessions} />
        ) : (
          <DataTable<AgentSession>
            columns={stuckColumns}
            data={stuckSessions}
            loading={sessionsLoading}
            emptyMessage={`No sessions stuck beyond ${STUCK_MINUTES} minutes`}
            emptyIcon="success"
          />
        )}
      </Box>

      {!anyLoading && (definitionsError || sessionsError) && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Some sections failed to load. Retry above to refresh them.
        </Typography>
      )}

      {/* Production-write confirm */}
      {dialogProps && (
        <QuillActionConfirm
          open={!!pending}
          title={dialogProps.title}
          message={dialogProps.message}
          caption={dialogProps.caption}
          confirmLabel={dialogProps.confirmLabel}
          destructive={dialogProps.destructive}
          loading={mutating}
          pendingLabel="Saving…"
          onConfirm={runPending}
          onCancel={closeDialog}
        />
      )}
    </Box>
  );
};

export default AgentsPage;
